<template>
  <login-layout :title="$t('Accedi alla tua Area Riservata')">
    <pf-alert v-if="route.query.welcome" inline variant="success" :title="$t('Benvenuto!')" class="pf-v6-u-mb-lg">
      <p v-if="route.query.welcome == '1'">
        {{ $t("La tua registrazione è stata completata con successo. Riceverai a breve un'email con le istruzioni per accedere.") }}
      </p>
      <p v-else>
        {{ $t("La tua registrazione è stata completata con successo.") }}
      </p>
    </pf-alert>

    <x-form
      :disabled="sending"
      :submit-text="$t('Log In')"
      submit-block
      @submit="submit"
    >
      <template v-if="requireTwoFA && (googleToken || (username && password))">
        <input v-if="googleToken" type="hidden" name="google_token" :value="googleToken">
        <template v-else>
          <input v-if="!invite?.azienda" type="hidden" name="username" :value="username">
          <input v-else type="hidden" name="username" :value="invite.username ? invite.username : invite.email">
          <input type="hidden" name="password" :value="password">
        </template>

        <pf-form-group :name="$t('Codice Authenticator')">
          <x-input
            id="logintwofa"
            v-model="codeTwoFA"
            name="totp"
            type="text"
            :placeholder="$t('Codice Authenticator')"
            required
          />
        </pf-form-group>
      </template>

      <template v-else>
        <pf-form-group :name="$t('Username')">
          <x-input
            v-if="!invite?.azienda"
            id="loginUsername"
            v-model="username"
            name="username"
            type="text"
            :placeholder="$t('Username')"
            required
            autocomplete="username"
          />
          <x-input
            v-else-if="invite.username"
            v-model="invite.username"
            name="username"
            type="text"
            :placeholder="$t('Username')"
            required
            readonly
          />
          <x-input
            v-else
            v-model="invite.email"
            name="username"
            type="text"
            :placeholder="$t('Username')"
            required
            readonly
          />
        </pf-form-group>

        <pf-form-group :name="$t('Password')">
          <x-input
            id="loginPassword"
            v-model="password"
            name="password"
            type="password"
            :placeholder="$t('Password')"
            required
            autocomplete="current-password"
          />
        </pf-form-group>

        <pf-form-group>
          <pf-checkbox name="rememberme" :label="$t('Ricordami per 30 giorni')" />
        </pf-form-group>

        <pf-alert v-if="resendLink" inline variant="warning" :title="$t('Indirizzo mail non confermato.')">
          <i18n-t
            keypath="Clicca sul link che ti abbiamo inviato per attivare l'accesso. {0}."
            tag="span"
          >
            <a :href="`/api/auth/reset?username=${username}&action=confirm`">Invia nuovamente il link</a>
          </i18n-t>
        </pf-alert>
      </template>
    </x-form>

    <div ref="scriptInjectElement" class="login-buttons">
      <div class="separator">
        <span>{{ $t('oppure') }}</span>
      </div>

      <div ref="googleLoginButton" />

      <a v-if="linkedInLoginUrl" :href="linkedInLoginUrl" class="btn-linkedin btn-block btn-lg" :class="{ disabled: sending }" @click="sending ? $event.preventDefault() : null">
        <img src="@/assets/linkedin-in.png" width="30" height="30" alt="LinkedIn" style="margin-right: 4px">
        <span>
          {{ $t('Accedi con LinkedIn') }}
        </span>
      </a>
    </div>

    <template #forgot-credentials>
      <router-link :to="{ name: 'resetpassword' }">{{ $t('Password dimenticata?') }}</router-link>
    </template>

    <template #signup>
      <p v-if="invite?.azienda">
        <i18n-t keypath="Invito ad accedere all'area riservata di {0}">
          <strong>{{ invite.azienda }}</strong>
        </i18n-t>
      </p>

      <p v-if="!invite?.username">
        {{ $t('Non hai ancora un account?') }}
        <router-link :to="{ name: 'signup' }">{{ $t('Registrati') }}</router-link>
      </p>
    </template>
  </login-layout>
</template>

<script lang="ts" setup>
import { http } from '../../http';
import { asyncComputed } from '@common/asyncComputed';
import { injectScript, pop, sleep } from '@common/utils';
import { loginDestination } from '../../utils';
import { Resource } from '@/resources';
import { onMounted, ref, useTemplateRef } from 'vue';
import type { Invite } from '@/typings/resources';
import axios from 'axios';
import { useOauthUrls } from './use';
import { $t } from '@/i18n';
import LoginLayout from '../../components/login-layout.vue';
import { useAlertStore, type AlertType } from '@/store/alerts';
import { useRoute, useRouter } from 'vue-router';

defineOptions({
  name: 'LoginPage',
});

const route = useRoute();
const router = useRouter();

const googleLoginButton = useTemplateRef('googleLoginButton');
const scriptInjectElement = useTemplateRef('scriptInjectElement');

const { linkedInLoginUrl } = useOauthUrls();

const { result: invite } = asyncComputed(async() => {
  const antk = pop(route.query.antk);
  if (!antk) {
    return {};
  }

  const invite = await new Resource<Invite>('invites').get(antk);

  if (invite.email && !invite.username) {
    router.push({
      name: 'signup',
      query: route.query,
    });
  }

  return invite;
}, null);

const sending = ref(false);
const requireTwoFA = ref(false);
const username = ref(pop(route.query.username) ?? '');
const password = ref('');
const googleToken = ref('');
const codeTwoFA = ref('');
const resendLink = ref(false);

onMounted(async() => {
  const alertStore = useAlertStore();

  const qRef = pop(route.query.ref);
  if (qRef) {
    localStorage.setItem('ref', qRef);
  }

  const msg = pop(route.query.msg);
  if (msg) {
    alertStore.add({title: msg, type: pop(route.query.msglvl) as AlertType ?? 'info', toast: true});
  }

  // try to use the provided token to login
  const username = pop(route.query.username);
  const token = pop(route.query.token);

  if (username && token) {
    sending.value = true;

    try {
      const data = new FormData();
      data.append('username', username);
      data.append('token', token);
      const referer = localStorage.getItem('ref');
      if (referer) {
        data.append('referer', referer);
      }

      await sleep(200); // php responds before its session is flushed to the storage. give it some time.
      const response = await http.post('/api/auth/loginwithtoken', data);

      if (response.data.result === 'ok') {
        alertStore.add({title: $t('Login effettuato con successo.'), type: 'success', toast: true});
        router.push(loginDestination({
          ref: response.data.referer,
          ...route.query,
        }));
      } else {
        router.push({
          name: 'login',
          query: {
            ...route.query,
            token: null,
          },
        });
      }
    } finally {
      sending.value = false;
    }
  } else  {
    initGoogleClient();
  }
});

async function initGoogleClient() {
  if (typeof import.meta.env.GOOGLE_CLIENT_ID !== 'string' || !scriptInjectElement.value) {
    return;
  }

  try {
    await injectScript('https://accounts.google.com/gsi/client', scriptInjectElement.value);
  } catch (e) {
    // Failed to load
    return;
  }

  const google: typeof import('google-one-tap') | undefined = (window as any).google;
  if (!google) {
    return;
  }

  google.accounts.id.initialize({
    client_id: import.meta.env.GOOGLE_CLIENT_ID,
    callback: googleCallback,
  });

  if (googleLoginButton.value) {
    google.accounts.id.renderButton(googleLoginButton.value, { theme: "outline", size: "large" });
  }

  google.accounts.id.prompt();
}

async function googleCallback(e: {credential?: string}) {
  if (!e.credential) {
    return;
  }
  googleToken.value = e.credential;
  const data = new FormData();
  data.append('google_token', e.credential);
  return await submitData(data);
}

async function submit(e: Event | undefined) {
  if (!(e?.target instanceof HTMLFormElement)) {
    return;
  }
  const data = new FormData(e.target);
  return await submitData(data);
}

async function submitData(data: FormData) {
  sending.value = true;
  resendLink.value = false;

  try {
    data.append('check', await fetchToken());
    const result = (await http.post('/api/auth/login', data)).data.result;

    if (result == 'totp') {
      requireTwoFA.value = true;
    } else if (result === 'mail-validation') {
      resendLink.value = true;
    } else if (result === 'ok') {
      router.push(loginDestination(route.query));
    }
  } catch (ex: any) {
    if (ex.response && axios.isAxiosError(ex) && ex.response.status === 401) {
      const alertStore = useAlertStore();
      alertStore.add({title: (ex.response.data as any).description, type: 'danger', toast: true});
    }
  } finally {
    sending.value = false;
  }
}

async function fetchToken() {
  return (await http.post('/api/auth/token')).data.token;
}
</script>
